import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { MotivationPartialData } from 'src/app/all-motivations/model/all-motivations.interface';
import { BaseComponent } from 'src/app/moov-common/base/base.component';
import { DriverEnergySliderComponent } from 'src/app/moov-common/components/driver-energy-slider/driver-energy-slider.component';
import { HorizontalScaleSliderComponent } from 'src/app/moov-common/components/horizontal-scale-slider/horizontal-scale-slider.component';
import { AbstractAuthorizationService } from 'src/app/moov-common/services/authorization/authorization.abstract.service';
import { ClaroEvents } from 'src/app/moov-common/services/google-analytics/analytics.enum';
import { InternalEventsManagerService } from 'src/app/moov-common/services/internal-events-manager/internal-events-manager.service';
import { AbstractMotivationsService } from 'src/app/moov-common/services/motivation/motivations.abstract.service';
import { UserMicroData_PulseDataExtended, UserRole } from 'src/app/moov-common/services/personal-user-data/personal-user-data';
import { PulsecheckFrquencyProfileEnum, PulseCheckRequiredData } from 'src/app/moov-common/services/responses/responses-data';
import { ResponsesAbstractService } from 'src/app/moov-common/services/responses/responses.abstract.service';

@Component({
  selector: 'app-pulsecheck-view',
  templateUrl: './pulsecheck-view.component.html',
  styleUrl: './pulsecheck-view.component.scss'
})
export class PulsecheckViewComponent extends BaseComponent implements OnInit{

    isLoading = true;
    userRespondedToAllQuestions = false;
    totalEnergyLevelScore = -1;
    teamLeadProgressScore = -1;
    driversScore : Record<string, number> = {};
    pulsecheckViewInteractionTypeInstance = PulsecheckViewInteractionType;
    userMotivations : MotivationPartialData[] = [];
    token: string = '';
    frequency: PulsecheckFrquencyProfileEnum = PulsecheckFrquencyProfileEnum.MONTHLY;
    frequencyText: string = '';
    isSubmissionCompletedSuccessfully = false;
    userDetailsRetrievedFromToken: UserMicroData_PulseDataExtended;
    
    
    @ViewChild('totalEnergyLevelSlider') private totalEnergyLevelSliderRef : HorizontalScaleSliderComponent;
    @ViewChild('teamLeadProgressSlider') private teamLeadProgressSliderRef : HorizontalScaleSliderComponent;
    @ViewChild('motivationsSliders') private motivationsSlidersRef : DriverEnergySliderComponent;
    userRolesIntsance = UserRole;

    private routerParamsSubscription: Subscription;

    constructor(public motivationsService: AbstractMotivationsService, 
                private responsesService: ResponsesAbstractService,
                private route: ActivatedRoute,
                private translateService: TranslateService,
                private authService: AbstractAuthorizationService,
                private internalEvents: InternalEventsManagerService) {
        super();
    }

    ngOnInit(): void {
      super.ngOnInit();

      this.isLoading = true;
      
      // get the token from the url
      this.routerParamsSubscription = this.route.queryParams.subscribe(
        (params: any) => {

          if (params['token']){
            this.token = params['token'];
          }
        
        // get the user data
        this.responsesService.getPulseCheckRequiredData(this.token).subscribe((data : PulseCheckRequiredData) => {
          // console.log(`data: ${JSON.stringify(data)}`);
            this.userMotivations = data.drivers;
            this.userDetailsRetrievedFromToken = data.userMicroData;
            this.frequency = data.frequency;

            if (this.frequency === PulsecheckFrquencyProfileEnum.MONTHLY) {
              this.frequencyText = this.translateService.instant('pulseCheckView.frequency_text.MONTHLY');
            }
            else if (this.frequency === PulsecheckFrquencyProfileEnum.WEEKLY) {
              this.frequencyText = this.translateService.instant('pulseCheckView.frequency_text.WEEKLY');
            }

            this.setPageLocale(data.userMicroData.locale);
            this.analytics.identify(data.userMicroData.id);
            this.analytics.sendClaroEvent(ClaroEvents.PulseCheck_viewed, {'frequency': this.frequency}, this.userDetailsRetrievedFromToken.id, this.userDetailsRetrievedFromToken.orgId);
            this.isLoading = false;
          });
        });
    }

    onValueChange(elementId: string, score: number) : void {
      // console.log(`onValueChange: ${elementId} - ${score}`);

      // store the score, verify if user is done
      if (elementId === PulsecheckViewInteractionType.TOTAL_ENERGY_LEVEL) {
        this.totalEnergyLevelScore = score;
      } else if (elementId === PulsecheckViewInteractionType.TEAM_LEAD_PROGRESS) {
        this.teamLeadProgressScore = score;
      } else {
        if (score > 0) {
          this.driversScore[elementId] = score;
        } else {
          delete this.driversScore[elementId];
        }
      }

      // console.log(`driversScore: ${JSON.stringify(this.driversScore)} lenght is ${Object.keys(this.driversScore).length} and userMotivations lenght is ${this.userMotivations.length} and userRole is ${this.userRole}`);

      // verify if user is done
      if (this.totalEnergyLevelScore !== -1 && Object.keys(this.driversScore).length === this.userMotivations.length) {
        if (this.userDetailsRetrievedFromToken.role === UserRole.MANAGER || this.userDetailsRetrievedFromToken.role === UserRole.HR) {
          // user is in a managerial role, so they need to answer the team lead progress question
          this.userRespondedToAllQuestions = (this.teamLeadProgressScore !== -1);
        }
        else{
          this.userRespondedToAllQuestions = true;
        }
      }
      else{
        this.userRespondedToAllQuestions = false;
      }
    }

    setPageLocale (locale: string): void{
      this.personalUserDataServiceForBaseClass.setLocale(locale) 
    }

    onSubmit() : void {
      // console.log(`onSubmit: ${JSON.stringify(this.driversScore)}`);

      this.analytics.sendClaroEvent(ClaroEvents.PulseCheck_submitted, {'frequency': this.frequency}, this.userDetailsRetrievedFromToken.id, this.userDetailsRetrievedFromToken.orgId);

      this.responsesService.sendPulseResponse_noAuth(this.token, this.totalEnergyLevelScore, this.driversScore, this.teamLeadProgressScore).subscribe((response) => {
        // console.log(`response: ${response}`);
        this.isSubmissionCompletedSuccessfully = true;
        this.internalEvents.pulseCheckCompleted.emit();

        // if the user is logged in, update the user details (get the updated energy levels)
        if (this.personalUserDataServiceForBaseClass.isUserLoggedIn()){
          this.authService.updateLoggedInUserToken().subscribe(()=> {
          });
        }
      });
    }

    copyUserMotivations() : void {
      this.user!.motivations.forEach((motivation) => {
        this.userMotivations.push({
          id: motivation,
          name: this.motivationsService.getMotivationName(motivation),
          shortDescription: '',
          imageUrl: this.motivationsService.getMotivationIconName(motivation),
          color: this.motivationsService.getMotivationColor(motivation)
        });
      });
    }
}

export enum PulsecheckViewInteractionType {
  TOTAL_ENERGY_LEVEL = 'TOTAL_ENERGY_LEVEL',
  TEAM_LEAD_PROGRESS = 'TEAM_LEAD_PROGRESS'
}
